import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Wallet, ShoppingBag, ArrowDownToLine, Hourglass, Loader2, CheckCircle2, XCircle, LayoutDashboard, ArrowRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import BackButton from '../components/BackButton';
import { useTranslation } from 'react-i18next';
import { getWalletSummary } from '../api/wallet';
import { pollTopupUntilTerminal } from '../api/walletTopupV2';
import { trackPixelEvent } from '../hooks/useMetaPixel';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { fmtCredits } from '../lib/currency';

// Same key ShopPage uses when kicking off a v2 checkout — see that file.
const V2_PENDING_KEY = 'nymia_pending_topup_v2';

const StatCard = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <div className="rounded-xl border border-border bg-card p-4" data-testid={testId}>
    <div className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg ${accent}`}>
      <Icon className="h-4 w-4" />
    </div>
    <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
    <p className="mt-1 text-2xl font-semibold tabular-nums text-foreground">{value}</p>
    {sub && <p className="mt-0.5 text-[11px] text-muted-foreground">{sub}</p>}
  </div>
);

/**
 * WalletPage — Phase F9.6 · riordino UI.
 * Il Portafoglio risponde a una sola domanda: "Quanti crediti ho?".
 * Payout, Stripe Connect, EarningsDashboard, estratto conto e rimborsi
 * sono stati RICOLLOCATI (non cancellati) nel Pannello di controllo
 * (/lives → tab Movimenti / Guadagni). Endpoint e logica invariati.
 */
const WalletPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const { refresh } = useAuth();
  const { t } = useTranslation('wallet');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  // Phase 37A.5 — v2 top-up return-flow status.
  //   phase: null | 'processing' | 'completed' | 'cancelled' | 'expired' | 'timeout' | 'error'
  const [topupV2, setTopupV2] = useState({ phase: null, credits: 0, eur: 0 });
  // Guards against React StrictMode double-invocation firing polling twice.
  const topupV2GuardRef = useRef(false);

  // F9.6 — i return URL di Stripe Connect (backend) puntano ancora a
  // /wallet; la configurazione prelievi ora vive nel Pannello di
  // controllo, quindi inoltriamo i parametri lì (frontend-only forward).
  const stripeForward =
    searchParams.get('stripe_connect') ||
    searchParams.get('mock_account') ||
    searchParams.get('stripe_dashboard');
  useEffect(() => {
    if (!stripeForward) return;
    navigate(`/lives?tab=earnings&${searchParams.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeForward]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setSummary(await getWalletSummary());
    } catch (_e) {
      toast({ title: t('errors.load_failed'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const h = () => load();
    window.addEventListener('nymia:data-changed', h);
    return () => window.removeEventListener('nymia:data-changed', h);
  }, [load]);

  // Phase 37A.5 — Handle return from Stripe Checkout (v2 flow). The
  // success/cancel URLs configured by /wallet/topup/checkout carry
  // `topup_success=1` or `topup_cancelled=1` plus our internal Nymia
  // session id. We poll the backend until the webhook flips the
  // session to a terminal state, then show a toast + refresh balance.
  useEffect(() => {
    if (topupV2GuardRef.current) return;
    const success = searchParams.get('topup_success');
    const cancelled = searchParams.get('topup_cancelled');
    const nymiaId = searchParams.get('nymia_id');
    if (!success && !cancelled) return;

    topupV2GuardRef.current = true;

    const clean = () => {
      try { sessionStorage.removeItem(V2_PENDING_KEY); } catch (_e) { /* ignore */ }
      navigate('/wallet', { replace: true });
    };

    if (cancelled) {
      setTopupV2({ phase: 'cancelled', credits: 0, eur: 0 });
      toast({
        title: t('topup_v2.cancelled_title'),
        description: t('topup_v2.no_credits_desc'),
      });
      clean();
      return;
    }

    // success=1
    let stashed = null;
    try {
      const raw = sessionStorage.getItem(V2_PENDING_KEY);
      if (raw) stashed = JSON.parse(raw);
    } catch (_e) { /* ignore */ }

    const internalId = nymiaId || stashed?.internal_session_id;
    if (!internalId) {
      toast({
        title: t('topup_v2.session_not_found_title'),
        description: t('topup_v2.session_not_found_desc'),
        variant: 'destructive',
      });
      clean();
      return;
    }

    setTopupV2({
      phase: 'processing',
      credits: stashed?.credits_amount || 0,
      eur: stashed?.eur_amount || 0,
    });

    (async () => {
      try {
        const finalStatus = await pollTopupUntilTerminal(internalId, (tick) => {
          setTopupV2((prev) => ({
            ...prev,
            credits: tick.credits_amount || prev.credits,
            eur: tick.eur_amount || prev.eur,
          }));
        });
        if (finalStatus?.status === 'completed') {
          setTopupV2({
            phase: 'completed',
            credits: finalStatus.credits_amount || 0,
            eur: finalStatus.eur_amount || 0,
          });
          // Phase 37E — Meta Purchase (wallet_topup). event_id matches
          // the server-side webhook fire so Meta deduplicates.
          trackPixelEvent(
            'Purchase',
            {
              value: Number(finalStatus.eur_amount || 0),
              currency: 'EUR',
              content_type: 'wallet_topup',
              content_ids: [`topup_${internalId}`],
            },
            `topup_${internalId}`,
          );
          clean();
          toast({
            title: t('topup_v2.success_title'),
            description: t('topup_v2.success_desc', { credits: finalStatus.credits_amount, eur: Number(finalStatus.eur_amount || 0).toFixed(2) }),
          });
          load();
          refresh?.();
        } else if (finalStatus?.status === 'cancelled' || finalStatus?.status === 'expired') {
          setTopupV2({ phase: finalStatus.status, credits: 0, eur: 0 });
          clean();
          toast({
            title: finalStatus.status === 'cancelled' ? t('topup_v2.cancelled_title') : t('topup_v2.expired_title'),
            description: t('topup_v2.no_credits_desc'),
            variant: 'destructive',
          });
        } else {
          setTopupV2({
            phase: 'timeout',
            credits: stashed?.credits_amount || 0,
            eur: stashed?.eur_amount || 0,
          });
          clean();
          toast({
            title: t('topup_v2.processing_toast_title'),
            description: t('topup_v2.processing_toast_desc'),
          });
        }
      } catch (err) {
        setTopupV2({ phase: 'error', credits: 0, eur: 0 });
        clean();
        toast({
          title: t('topup_v2.error_title'),
          description: err?.response?.data?.detail?.split?.('|')?.pop?.() || t('topup_v2.verify_error_default'),
          variant: 'destructive',
        });
      }
    })();
  }, [searchParams]);

  if (loading) {
    return (
      <div className="flex h-[60vh] items-center justify-center">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-neutral-800" />
      </div>
    );
  }
  if (!summary) return null;

  return (
    <div className="mx-auto max-w-[935px] px-4 pt-6 pb-24 md:pt-10">
      <BackButton className="mb-4" />
      <div className="mb-6">
        <h1 className="text-2xl font-semibold">{t('page.title')}</h1>
        <p className="text-sm text-muted-foreground">
          {t('page.subtitle')}
        </p>
      </div>

      {/* CTA principale "Acquista crediti" → Shop (invariata) */}
      <button
        type="button"
        onClick={() => navigate('/shop')}
        className="mb-6 w-full rounded-2xl gold-gradient p-5 text-left shadow-lg transition-transform hover:scale-[1.01] active:scale-[0.99]"
        data-testid="topup-primary-cta"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-black/20">
              <ShoppingBag className="h-5 w-5 text-black" />
            </div>
            <div>
              <p className="text-base font-bold text-black md:text-lg" data-testid="topup-primary-cta-title">
                {t('page.buy_credits')}
              </p>
              <p className="mt-0.5 text-[12px] text-black/80" data-testid="topup-primary-cta-subtitle">
                {t('page.buy_credits_subtitle')}
              </p>
            </div>
          </div>
          <ArrowDownToLine className="h-5 w-5 shrink-0 rotate-[-90deg] text-black" />
        </div>
      </button>

      {/* Phase 37A.5 — v2 top-up return banner */}
      {topupV2.phase === 'processing' && (
        <div
          className="mb-6 flex items-center gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4"
          data-testid="topup-v2-processing"
        >
          <Loader2 className="h-5 w-5 shrink-0 animate-spin gold-text" />
          <div>
            <p className="text-sm font-semibold gold-text">{t('topup_v2.processing_title')}</p>
            <p className="text-xs text-muted-foreground">
              {t('topup_v2.processing_desc')}
            </p>
          </div>
        </div>
      )}
      {topupV2.phase === 'completed' && (
        <div
          className="mb-6 flex items-center gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4"
          data-testid="topup-v2-success-banner"
        >
          <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-300" />
          <div>
            <p className="text-sm font-semibold text-emerald-200">{t('topup_v2.success_title')}</p>
            <p className="text-xs text-muted-foreground">
              {t('topup_v2.success_desc', { credits: topupV2.credits, eur: Number(topupV2.eur || 0).toFixed(2) })}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setTopupV2({ phase: null, credits: 0, eur: 0 })}
            className="ml-auto text-xs text-emerald-200/70 hover:text-emerald-200"
            data-testid="topup-v2-success-dismiss"
          >
            {t('topup_v2.dismiss')}
          </button>
        </div>
      )}
      {(topupV2.phase === 'cancelled' || topupV2.phase === 'expired' || topupV2.phase === 'error') && (
        <div
          className="mb-6 flex items-center gap-3 rounded-2xl border border-red-500/40 bg-red-500/10 p-4"
          data-testid="topup-v2-error-banner"
        >
          <XCircle className="h-5 w-5 shrink-0 text-red-300" />
          <div>
            <p className="text-sm font-semibold text-red-200">
              {topupV2.phase === 'cancelled' && t('topup_v2.cancelled_title')}
              {topupV2.phase === 'expired' && t('topup_v2.expired_title')}
              {topupV2.phase === 'error' && t('topup_v2.error_title')}
            </p>
            <p className="text-xs text-muted-foreground">
              {t('topup_v2.no_credits_desc')}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setTopupV2({ phase: null, credits: 0, eur: 0 })}
            className="ml-auto text-xs text-red-200/70 hover:text-red-200"
            data-testid="topup-v2-error-dismiss"
          >
            {t('topup_v2.dismiss')}
          </button>
        </div>
      )}
      {topupV2.phase === 'timeout' && (
        <div
          className="mb-6 flex items-center gap-3 rounded-2xl border border-amber-500/40 bg-amber-500/10 p-4"
          data-testid="topup-v2-timeout-banner"
        >
          <Hourglass className="h-5 w-5 shrink-0 gold-text" />
          <div>
            <p className="text-sm font-semibold gold-text">{t('topup_v2.timeout_title')}</p>
            <p className="text-xs text-muted-foreground">
              {t('topup_v2.timeout_desc')}
            </p>
          </div>
        </div>
      )}

      {/* F9.6 — saldo essenziale: disponibili + acquistati. Tutto il resto
          (maturazione, guadagni, payout, ledger) vive nel Pannello. */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <StatCard
          icon={Wallet}
          label={t('kpi.available')}
          value={fmtCredits(summary.available_balance)}
          sub={`≈ €${summary.available_balance_eur.toFixed(2)}`}
          accent="bg-emerald-500/20 text-emerald-300"
          testId="wallet-kpi-available"
        />
        <StatCard
          icon={ShoppingBag}
          label={t('kpi.purchased')}
          value={fmtCredits(summary.total_purchased)}
          sub={`≈ €${summary.total_purchased_eur.toFixed(2)}`}
          accent="bg-amber-500/20 gold-text"
          testId="wallet-kpi-purchased"
        />
      </div>

      {/* F9.6 — rimando al Pannello di controllo per il monitoraggio */}
      <button
        type="button"
        onClick={() => navigate('/lives?tab=movements')}
        className="flex w-full items-center justify-between gap-3 rounded-2xl border border-border bg-card p-4 text-left transition hover:bg-secondary"
        data-testid="wallet-open-panel-btn"
      >
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-secondary gold-text">
            <LayoutDashboard className="h-4 w-4" />
          </div>
          <p className="text-sm text-muted-foreground">{t('page.panel_hint')}</p>
        </div>
        <span className="flex shrink-0 items-center gap-1 text-xs font-semibold gold-text">
          {t('page.open_panel')} <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </button>
    </div>
  );
};

export default WalletPage;
